import * as React from 'react';
import { I18n } from 'react-redux-i18n';

import { MODULE_NAME } from '@modules/fhq-design-tool/constants/shared';
import { SkipLogic } from '@common/components/skip-logic';
import { SkipLogicPredefinedControlType, SkipLogicProps } from '@common/components/skip-logic/skip-logic.types';
import { FieldSelectorGetFieldParams } from '@common/components/FieldSelector/field-selector.types';
import { NOT_ALLOWED_FOR_PAGE_ADD_FIELD_TYPES } from '@modules/fhq-design-tool/constants/page';
import { Flex } from '@common/components/Flex';
import { Button } from '@common/components/Button';
import { FieldSelector } from '@common/components/FieldSelector';

import { Subtitle } from '../../../subtitle';
import { RadioGroup, RadioGroupValue } from '../../../radio-group';
import { Title } from '../../../title';
import { Field } from '../../../questionnaire-designer/components/field';
import { TextBlock } from '../../../questionnaire-designer/components/text-block';

import { usePageController } from './page.controller';
import { QuestionnaireConsentPageProps } from './page.types';

import { FieldsWithTextBlocks } from '../../../fields-with-text-blocks';
import { FieldInList } from '../../../fields-with-text-blocks/fields-with-text-blocks.types';
import { QuestionnaireTextBlockLocalDto } from '@modules/fhq-design-tool/dto/text-block-create-dto';

import './page.styles.scss';

export const Page: React.FC<QuestionnaireConsentPageProps> = (props) => {
  const { cachedData, showConfirmMessage, getFields, getFolders, scrollParentToTop, errors, questionnaireId, consentPageNumber } = props;

  const {
    familyPagesSkipLogicConditions,
    handleSkipLogicChange,
    handleSkipLogicConditionAdd,
    handleUseSkipLogicChange,
    handleFieldChange,
    buttonsSkipLogicConditions,
    handleConsentButtonsSkipLogicChange,
    handleConsentButtonsSkipLogicConditionAdd,
    handleUseConsentButtonsSkipLogicChange,
    goBackFromFieldEdit,
    editingField,
    handleFieldAdd,
    openFieldChooser,
    closeFieldChooser,
    fieldChooserOpened,
    navigateOnFieldEdit,
    canShowHideField,
    editingTextBlock,
    openTextBlockEditing,
    closeTextBlockEditing,
    handleTextBlockChange,
    handleFieldsOrTextBlocksChange,
    handleAddTextBlock,
    combinedItems
  } = usePageController(props);

  if (editingTextBlock) {
    return (
      <TextBlock
        data={editingTextBlock}
        onGoBack={closeTextBlockEditing}
        getFieldsForSkipLogic={getFields}
        getFoldersForSkipLogic={getFolders}
        onChange={handleTextBlockChange}
        errors={errors?.consentPage?.texts?.[editingTextBlock.id]}
      />
    );
  }

  if (editingField) {
    return (
      <Field
        errors={errors?.consentPage?.fields ? errors?.consentPage?.fields[editingField.progenyId] : undefined}
        scrollParentToTop={scrollParentToTop}
        data={editingField}
        onGoBack={goBackFromFieldEdit}
        onChange={handleFieldChange}
        showConfirmMessage={showConfirmMessage}
        getFieldsForSkipLogic={getFields}
        getFoldersForSkipLogic={getFolders}
        pageNumber={consentPageNumber}
        questionnaireId={questionnaireId}
      />
    );
  }

  return (
    <div className="FhqDesignToolQuestionnaireConsentPage">
      <Title text={I18n.t('Consent Page Skip Logic')} />
      <Subtitle
        text={I18n.t('skipLogicDescriptionPage').split('**').map((part, i) =>
          i % 2 === 1 ? <strong key={i}>{part}</strong> : part
        )}
      />
      <RadioGroup
        margin="12px 0 12px 0"
        value={cachedData?.page?.skipLogicEnabled ? "true" : "false"}
        options={[
          { id: "false", name: I18n.t("Show Always") },
          { id: "true", name: I18n.t("Show only if condition below is true") },
        ]}
        onChange={handleUseSkipLogicChange as (v: RadioGroupValue) => void}
        metadata={{
          description: "page-use-skip-logic",
        }}
      />
      {cachedData?.page?.skipLogicEnabled && (
        <SkipLogic
          moduleName={MODULE_NAME}
          className="FhqDesignToolQuestionnaireConsentPage__skip-logic"
          conditions={familyPagesSkipLogicConditions}
          onRemove={handleSkipLogicChange}
          onChange={handleSkipLogicChange}
          onAdd={handleSkipLogicConditionAdd as SkipLogicProps['onAdd']}
          getFolders={getFolders}
          getFields={getFields}
          controls={[
            { type: SkipLogicPredefinedControlType.moveUp, handler: handleSkipLogicChange },
            { type: SkipLogicPredefinedControlType.moveDown, handler: handleSkipLogicChange },
            { type: SkipLogicPredefinedControlType.add },
          ]} 
        />
      )}
      <Flex alignItems="center" className="FhqDesignToolQuestionnaireConsentPage__add-field-header">
        <Title text={I18n.t('Fields')} margin={0} />
        <Button bsStyle="primary" className="FhqDesignToolQuestionnaireConsentPage__add-field-btn" onClick={openFieldChooser}>
          +
        </Button>
      </Flex>
      <Flex alignItems="center" className="FhqDesignToolQuestionnaireConsentPage__add-field-header">
        <Title text={I18n.t('Text')} margin={0} />
        <Button bsStyle="primary" className="FhqDesignToolQuestionnaireConsentPage__add-field-btn" onClick={handleAddTextBlock}>
          +
        </Button>
      </Flex>
      <div className="FhqDesignToolQuestionnaireConsentPage__print-save-header">
        <Title text={I18n.t('Consent Print/Accept Button Skip Logic')} />
        <Subtitle
          text={I18n.t('skipPrintLogicDescriptionPage').split('**').map((part, i) =>
            i % 2 === 1 ? <strong key={i}>{part}</strong> : part
          )}
        />
        <RadioGroup
          margin="12px 0 12px 0"
        value={cachedData?.useConsentButtonsSkipLogic === true ? "true" : "false"}
          options={[
            { id: "false", name: I18n.t("Enable Always") },
            { id: "true", name: I18n.t("Enable only if condition below is true") },
          ]}
          onChange={handleUseConsentButtonsSkipLogicChange as (v: RadioGroupValue) => void}
          metadata={{ description: "consent-buttons-use-skip-logic" }}
        />
      {cachedData?.useConsentButtonsSkipLogic === true && (
        <SkipLogic
          moduleName={MODULE_NAME}
          className="FhqDesignToolQuestionnaireConsentPage__skip-logic"
          conditions={buttonsSkipLogicConditions}
          onRemove={handleConsentButtonsSkipLogicChange}
          onChange={handleConsentButtonsSkipLogicChange}
          onAdd={handleConsentButtonsSkipLogicConditionAdd as SkipLogicProps["onAdd"]}
          getFolders={getFolders}
          getFields={getFields}
          controls={[
            { type: SkipLogicPredefinedControlType.moveUp, handler: handleConsentButtonsSkipLogicChange },
            { type: SkipLogicPredefinedControlType.moveDown, handler: handleConsentButtonsSkipLogicChange },
            { type: SkipLogicPredefinedControlType.add },
          ]}
        />
      )}
     </div>
      {questionnaireId && <FieldsWithTextBlocks<FieldInList, QuestionnaireTextBlockLocalDto>
        combinedItems={combinedItems}
        onChange={handleFieldsOrTextBlocksChange}
        fieldProps={{
          hasError: (x) => errors?.consentPage?.fields ? Boolean(errors?.consentPage?.fields[x.original.progenyId]) : false,
          onEdit: navigateOnFieldEdit,
          showDeleteConfirmMessage: showConfirmMessage,
          canShowHideField,
          pageNumber: consentPageNumber,
          questionnaireId
        }}
        textBlocksProps={{
          onEdit: openTextBlockEditing,
          showDeleteConfirmMessage: showConfirmMessage,
          hasError: x => Boolean(errors?.consentPage?.texts?.[x.id]),
        }}
      />}
      {fieldChooserOpened && (
        <FieldSelector
          withSearch
          getFolders={getFolders}
          getFields={({ searchKeyword, ...restParams }: FieldSelectorGetFieldParams) =>
            getFields({ searchQuery: searchKeyword, ...restParams, includeTables: true })
              .then((fields) =>
                fields.filter((x) => !NOT_ALLOWED_FOR_PAGE_ADD_FIELD_TYPES.includes(x.fieldTypeId))
              )
          }
          onChange={handleFieldAdd}
          close={closeFieldChooser}
          closeOnChange={false}
          multiple
        />
      )}
    </div>
  );
};